import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { useFilters } from '../context/filter-context/FilterContext';
import { fetchTocNodes, NodeWithoutDescription } from '../../../../api/table-of-content';
import useThrowAsyncError from '../../../../hooks/useThrowAsyncError';

interface TOCContextType {
  toc: NodeWithoutDescription[];
  loading: boolean;
}

export const TOCDataContext = createContext<TOCContextType | undefined>(undefined);

export function TOCDataContextProvider({ children }: PropsWithChildren) {
  const { selectedModule } = useFilters();
  const [loading, setIsLoading] = useState(false);
  const [toc, setToc] = useState<NodeWithoutDescription[]>([]);
  const handleError = useThrowAsyncError();

  useEffect(() => {
    if (!selectedModule) return;

    if (selectedModule.isVirtualExerciseModule) {
      setToc([]);
      setIsLoading(false);
      return;
    }

    let current = true;

    setIsLoading(true);

    fetchTocNodes(selectedModule.id)
      .then(res => {
        if (current) {
          setToc(res.data);
        }
      })
      .catch(handleError)
      .finally(() => {
        if (current) {
          setIsLoading(false);
        }
      });

    return () => {
      current = false;
    };
  }, [handleError, selectedModule]);

  return <TOCDataContext.Provider value={{ toc, loading }}>{children}</TOCDataContext.Provider>;
}

export function useTableOfContent() {
  const context = useContext(TOCDataContext);

  if (!context) {
    throw new Error('useTableOfContent must be used within a TOCDataContextProvider');
  }

  return context;
}
